/**
 * Lines shown while a Mitti starts. Four kinds: progress, kushti culture,
 * generic Henoyo facts, and one tip per pack the learner has. Keep each line
 * short. Culture lines are the widely known ones. Henoyo lines stay generic:
 * no customer names, no invented numbers.
 */

export type TriviaKind = "progress" | "culture" | "henoyo" | "pack";

export interface StartupTrivia {
  text: string;
  kind: TriviaKind;
  /** Set only for kind "pack". Shown when the learner has that pack. */
  pack?: "irm" | "apisec" | "lms";
}

export const STARTUP_TRIVIA: readonly StartupTrivia[] = [
  { kind: "progress", text: "The mitti rests when you stop. Come back, and it wakes." },
  { kind: "progress", text: "Waking your server…" },
  { kind: "progress", text: "Turning the mitti… your Mehnat starts in up to 3 minutes" },
  { kind: "progress", text: "Securing your connection…" },
  { kind: "progress", text: "Almost there…" },
  { kind: "progress", text: "Checking that your database is up…" },
  { kind: "progress", text: "The lock on the gate is part of the start." },

  { kind: "culture", text: "A mitti is the clay pit a pehlwan trains in." },
  { kind: "culture", text: "Pehlwans turn the mitti each morning so the earth is soft." },
  { kind: "culture", text: "Turning the mitti loosens the soil and starts the day's practice." },
  { kind: "culture", text: "Ghisai is the daily grind: ordinary work, repeated." },
  { kind: "culture", text: "Dand-Baithak is the classic drill: push-ups and deep squats." },
  { kind: "culture", text: "Akhara legends are said to have done thousands of dands (push-ups) and baithaks (squats) a day." },
  { kind: "culture", text: "An ustad is the coach. A shagird is the student." },
  { kind: "culture", text: "The khalifa is the head of an akhara." },
  { kind: "culture", text: "Zor is strength. In the akhara it is earned, not announced." },
  { kind: "culture", text: "A dangal is a wrestling meet, where the practice is shown." },
  { kind: "culture", text: "Kushti is the wrestling of the akhara. The mitti is the place." },
  { kind: "culture", text: "An akhara is a school: a place, a teacher, and a daily routine." },
  { kind: "culture", text: "Mehnat is the session of work. The mitti is where you do it." },

  { kind: "henoyo", text: "Henoyo is a platform for building enterprise apps." },
  { kind: "henoyo", text: "Solution packs add apps such as IRM, API Security, and LMS." },
  { kind: "henoyo", text: "Henoyo Core is the platform. Packs sit on the same site." },
  { kind: "henoyo", text: "A pack is another app on your Mitti, not a separate login." },
  { kind: "henoyo", text: "You sign in once on Akhara. The Mitti opens from there." },
  { kind: "henoyo", text: "Your Khalifa chooses the packs. You put in the Mehnat." },

  { kind: "pack", pack: "irm", text: "IRM tip: start with one risk, name an owner, write the treatment." },
  { kind: "pack", pack: "irm", text: "IRM tip: an exception needs an owner and a date." },
  { kind: "pack", pack: "apisec", text: "API Security tip: list the APIs you run before the findings." },
  { kind: "pack", pack: "apisec", text: "API Security tip: a finding without an owner just sits there." },
  { kind: "pack", pack: "lms", text: "LMS tip: finish one lesson before you build a catalogue." },
  { kind: "pack", pack: "lms", text: "LMS tip: progress is lessons finished, not time on the page." },
];

/**
 * Ready-email sign-off. functions/src/train-env-mail.ts copies these eight
 * culture lines and this FNV-1a hash, because functions cannot import src/.
 */
const SIGNOFF_COUNT = 8;

function signoffIndex(slug: string, count: number): number {
  let hash = 2166136261;
  for (let i = 0; i < slug.length; i += 1) {
    hash ^= slug.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) % count;
}

/** One culture line for this Mitti. The same slug always returns the same line. */
export function pickSignoff(slug: string): string {
  const lines = STARTUP_TRIVIA.filter((item) => item.kind === "culture")
    .slice(0, SIGNOFF_COUNT)
    .map((item) => item.text);
  return lines[signoffIndex(slug, lines.length)] ?? lines[0];
}

/** Progress, culture, and Henoyo lines, plus tips for packs this learner has. */
export function triviaForPacks(packs: readonly string[]): string[] {
  const have = new Set(packs);
  return STARTUP_TRIVIA.filter(
    (item) => item.kind !== "pack" || (item.pack !== undefined && have.has(item.pack)),
  ).map((item) => item.text);
}
